/**
 * @typedef {import("../../types/directus").components["schemas"]["ItemsArticles"]} DirectusArticle
 */
import { useQuery } from "@tanstack/react-query";
import { getJson } from "../client";

export function useArticle(identifier, options = {}) {
  const { preview = false, ...queryOptions } = options;
  const isValidId = Number.isFinite(Number(identifier));
  return useQuery({
    queryKey: ["article", identifier, preview],
    enabled: isValidId,
    /** @returns {Promise<DirectusArticle | null>} */
    queryFn: async () => {
      const fields = [
        "id",
        "status",
        "cover_image",
        "read_time",
        "published_at",
        "featured",
        // Countries - fetch nested data
        "countries.Countries_id.id",
        "countries.Countries_id.key",
        "countries.Countries_id.translations.name",
        "countries.Countries_id.translations.languages_code",
        // Translations - fetch nested data
        "translations.id",
        "translations.title",
        "translations.content",
        "translations.languages_code",
        // Author via junction
        "author.id",
        "author.Author_id.id",
        "author.Author_id.first_name",
        "author.Author_id.last_name",
        "author.Author_id.translations.id",
        "author.Author_id.translations.languages_code",
        "author.Author_id.translations.description",
        "author.Author_id.avatar",
        "author.Author_id.instagram",
        "author.Author_id.face",
        // Tags via junction
        "tags.id",
        "tags.Tags_id.id",
        "tags.Tags_id.translations",
      ].join(",");

      const numericId = Number(identifier);
      const filter = { id: { _eq: numericId } };
      
      // Only filter by published status if not in preview mode
      if (!preview) {
        filter.status = { _eq: "published" };
      }

      const params = {
        fields,
        limit: "1",
        filter,
      };

      const json = await getJson("/items/Articles", params);
      return json?.data?.[0] || null;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
}

export default useArticle;
